import { BaseWebComponentView } from '../../../framework/BaseWebComponentView';
import { GzImageRepoWebComponent } from '../../../ui/gzImageRepo';
import { NoteEditorWebComponent } from '../../noteEditor';
import { TemplateState } from '../../../types/common/types';
import { GzButtonWebComponent } from '../../../ui/gzButton';
import { hideElement, showElement, toggleVisibility } from '../../../utils/htmlElement.utils';
import { NoteCardWebComponent } from '../../noteCard';
import { GzTitleWebComponent } from '../../../ui/gzTitle';
import { GzTextWebComponent } from '../../../ui/gzText';
import { UpdateNoteRequest, NotesListViewData } from '../types';
import { NoteDisplayMode } from '../../noteEditor/types';
import { Note } from '../../types';

export class NotesListView extends BaseWebComponentView {
  public dom: {
    $notesListContainer: HTMLDivElement;
    $overlay: HTMLDivElement;
    $backButton: GzButtonWebComponent;
    $backButtonText: HTMLSpanElement;
    $notesListTitle: GzTitleWebComponent;
    $emptyStateContainer: HTMLDivElement;
    $emptyStateImage: GzImageRepoWebComponent;
    $emptyStateTitle: GzTitleWebComponent;
    $emptyStateText: GzTextWebComponent;
    $emptyStateAddNoteButton: GzButtonWebComponent;
    $notesList: HTMLDivElement;
    $notesContainer: HTMLDivElement;
    $addNoteButton: GzButtonWebComponent;
    $noteEditorContainer: HTMLDivElement;
  };

  public setTranslatedStrings(): void {
    this.dom.$backButtonText.innerText = this.dependencies.translateFn('Back');
    this.dom.$notesListTitle.textContent = this.dependencies.translateFn('Analyst notes');
    this.dom.$emptyStateTitle.innerHTML = this.dependencies.translateFn('Start adding notes');
    this.dom.$emptyStateText.innerHTML = this.dependencies.translateFn("You don't have any notes yet");
    this.dom.$emptyStateAddNoteButton.innerText = this.dependencies.translateFn('Add note');
    this.dom.$addNoteButton.innerText = this.dependencies.translateFn('Add note');
  }

  public getDomElements(): void {
    this.dom.$notesListContainer = this.shadow.querySelector('.notes-list-container');
    this.dom.$overlay = this.shadow.querySelector('.overlay');
    this.dom.$backButton = this.shadow.querySelector('.back-btn');
    this.dom.$backButtonText = this.shadow.querySelector('.back-btn-text');
    this.dom.$notesListTitle = this.shadow.querySelector('.notes-list-title');
    this.dom.$emptyStateContainer = this.shadow.querySelector('.empty-state-container');
    this.dom.$emptyStateImage = this.shadow.querySelector('gz-image-repo');
    this.dom.$emptyStateTitle = this.shadow.querySelector('.empty-state-title');
    this.dom.$emptyStateText = this.shadow.querySelector('.empty-state-text');
    this.dom.$emptyStateAddNoteButton = this.shadow.querySelector('.empty-state-add-note-button');
    this.dom.$notesList = this.shadow.querySelector('.notes-list');
    this.dom.$notesContainer = this.shadow.querySelector('.notes-container');
    this.dom.$addNoteButton = this.shadow.querySelector('.add-note-button');
    this.dom.$noteEditorContainer = this.shadow.querySelector('.note-editor-container');

    this.setTranslatedStrings();
  }

  public async buildSuccessView(data: NotesListViewData): Promise<void> {
    toggleVisibility(this.dom.$backButton, data.isBackButtonEnabled);

    if (data.isBackButtonEnabled) {
      this.setElementClass(this.dom.$notesListTitle, 'with-back-btn');
    }

    if (data.notes.length > 0) {
      this.toggleEmptyStateVisibility(false);

      const addNoteCardPromises: Promise<void>[] = [];

      for (const note of data.notes) {
        addNoteCardPromises.push(this.addNoteCard(note));
      }

      await Promise.all(addNoteCardPromises);
    } else {
      this.toggleEmptyStateVisibility(true);
    }
  }

  public buildErrorView(error: Error): void {
    this.resetTemplateState(this.dom.$notesListContainer);
    this.setTemplateState(this.dom.$notesListContainer, TemplateState.ERROR);
  }

  public setLoadingState(): void {
    this.resetTemplateState(this.dom.$notesListContainer);
    this.setTemplateState(this.dom.$notesListContainer, TemplateState.LOADING);
  }

  public removeLoadingState(): void {
    this.resetTemplateState(this.dom.$notesListContainer);
  }

  public async showNoteEditor(note: UpdateNoteRequest | null, displayMode: NoteDisplayMode): Promise<void> {
    const noteEditor = new NoteEditorWebComponent();

    noteEditor.note = note ?? {};
    noteEditor.displayMode = displayMode;
    noteEditor.injectDependencies(this.dependencies);

    await noteEditor.start();

    this.dom.$noteEditorContainer.appendChild(noteEditor);
  }

  public toggleOverlayVisibility(visible: boolean): void {
    if (visible) {
      showElement(this.dom.$overlay);
    } else {
      hideElement(this.dom.$overlay);
    }
  }

  public removeNoteEditor(): void {
    this.dom.$noteEditorContainer.innerHTML = '';
  }

  public removeNotes(): void {
    this.dom.$notesContainer.innerHTML = '';
  }

  private toggleEmptyStateVisibility(visible: boolean): void {
    toggleVisibility(this.dom.$emptyStateContainer, visible);
    toggleVisibility(this.dom.$notesList, !visible);
  }

  private async addNoteCard(note: Note): Promise<void> {
    const noteCard = new NoteCardWebComponent();

    noteCard.note = note;
    noteCard.injectDependencies(this.dependencies);

    await noteCard.start();

    this.dom.$notesContainer.appendChild(noteCard);
  }
}
